// ============================================================================
// DocumentShell — Layout padrão de 3 colunas compartilhado entre todos os
// motores de documentos (Contratos, Procurações, Declarações, Petições).
//
// Estrutura:
//   <DocumentShell>
//     ├── LeftSidebar  — header escuro + tabs numeradas + toggle + scrollable content + action bar
//     ├── CenterPanel  — papel A4 centralizado, overflow-auto
//     └── RightPanel   — mapa do documento (lista de blocos + footer)
//
// Uso:
//   <DocumentShell
//     title="Motor de Declarações"
//     tabs={[{ id, label }]}
//     activeTab={openSection}
//     onTabChange={setOpenSection}
//     mode={mode}
//     onModeChange={setMode}
//     actionBar={<>...</>}
//     rightPanelTitle="Mapa da Declaração"
//     rightPanelCount={5}
//     rightPanelItems={[{ id, label, number, isActive, onDelete, onClick }]}
//     syncState={cloudSyncState}
//     syncLabel={syncLabel}
//     error={error}
//   >
//     {/* conteúdo scrollável do sidebar (seções accordion) */}
//   </DocumentShell>
// ============================================================================

import React from 'react';
import { AlertCircle, GripVertical, Map as MapIcon, Trash2 } from 'lucide-react';

// ── ConfigSection ────────────────────────────────────────────────────────────
// Accordion idêntico ao usado em ProcurationEngine e ContractEngine.
export const ConfigSection = ({ id, title, icon, children, openSection, setOpenSection }) => {
  const isOpen = openSection === id;
  return (
    <div className="border-b border-gray-200">
      <button
        onClick={() => setOpenSection(isOpen ? null : id)}
        className={`w-full flex items-center justify-between px-2.5 py-1.5 text-left transition-colors border-l-2 ${isOpen ? 'bg-slate-50 border-l-[#C5A059]' : 'bg-white border-l-transparent hover:bg-slate-50'}`}
        type="button"
      >
        <span className={`text-[9px] font-medium uppercase tracking-[0.03em] flex items-center gap-1.5 ${isOpen ? 'text-[#20243B]' : 'text-slate-500'}`}>
          <i className={`fas ${icon} ${isOpen ? 'text-[#C5A059]' : 'text-slate-300'}`}></i>
          {title}
        </span>
        <i className={`fas fa-chevron-down text-[9px] text-slate-300 transition-transform ${isOpen ? 'rotate-180' : ''}`}></i>
      </button>
      {isOpen && <div className="px-2.5 py-1.5 bg-slate-50/80 space-y-2 animate-fade-in">{children}</div>}
    </div>
  );
};

// ── RightPanel (DocumentMap) ─────────────────────────────────────────────────
export const DocumentMapPanel = ({ title = 'Mapa do Documento', items = [], onItemClick, onItemDelete }) => (
  <div className="hidden xl:flex w-72 2xl:w-96 bg-white border-l border-gray-200 h-full flex-col shadow-xl z-20 flex-shrink-0">
    <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
      <h3 className="text-[10px] font-bold text-gray-500 uppercase tracking-wider flex items-center gap-2">
        <MapIcon className="w-3 h-3" /> {title}
      </h3>
      <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">{items.length} Blocos</span>
    </div>
    <div className="flex-1 overflow-y-auto p-2 custom-scrollbar space-y-1">
      {items.map((item, idx) => (
        <div
          key={item.id ?? idx}
          draggable={!!item.draggable}
          onClick={() => onItemClick?.(item)}
          className={`relative pl-2 py-2 pr-2 cursor-pointer text-xs transition-all group rounded border border-transparent flex items-center gap-2 ${item.isActive ? 'bg-blue-50 border-blue-200 text-[#20243B] font-bold shadow-sm' : 'hover:bg-gray-50 text-gray-600 hover:border-gray-200'}`}
        >
          <div className="text-gray-300 cursor-grab hover:text-gray-500"><GripVertical className="w-3 h-3" /></div>
          <div className="w-6 text-[10px] font-bold text-gray-300 text-right select-none">{idx + 1}</div>
          <div className="flex-1 truncate">{item.label}</div>
          {onItemDelete && (
            <button
              onClick={(e) => { e.stopPropagation(); onItemDelete?.(item); }}
              className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 p-1 rounded hover:bg-white transition-all"
              title="Excluir"
              type="button"
            ><Trash2 className="w-3 h-3" /></button>
          )}
        </div>
      ))}
    </div>
    <div className="p-2 bg-gray-50 text-[9px] text-center text-gray-400 border-t border-gray-200">Arraste para reordenar</div>
  </div>
);

// ── SyncBadge ────────────────────────────────────────────────────────────────
const SyncBadge = ({ state, meta }) => {
  const badge =
    state === 'saving' ? { cls: 'border-sky-200 bg-sky-50 text-sky-700', label: 'Salvando...' } :
    state === 'saved'  ? { cls: 'border-emerald-200 bg-emerald-50 text-emerald-700', label: `Nuvem OK${meta?.updatedAt ? ` · ${new Date(meta.updatedAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : ''}` } :
    state === 'error'  ? { cls: 'border-red-200 bg-red-50 text-red-700', label: 'Falha no auto-save' } :
    { cls: 'border-gray-200 bg-gray-50 text-gray-600', label: 'Aguardando alterações' };
  return (
    <span className={`px-2 py-1 rounded-full border whitespace-nowrap text-[9px] font-semibold uppercase tracking-[0.04em] ${badge.cls}`}>
      {badge.label}
    </span>
  );
};

// ── DocumentShell (componente principal) ─────────────────────────────────────
export const DocumentShell = ({
  title,
  tabs = [],
  activeTab,
  onTabChange,
  mode = 'producao',
  onModeChange,
  children,
  actionBar,
  syncState = 'idle',
  syncMeta = null,
  error = '',
  rightPanelTitle,
  rightPanelItems = [],
  onRightPanelItemClick,
  onRightPanelItemDelete,
  centerPanel,
  rightPanel,
}) => (
  <div className="flex flex-col lg:flex-row h-[calc(100vh-64px)] font-sans bg-gray-100 overflow-hidden print:h-auto print:overflow-visible w-full z-0 relative">

    {/* ══ LEFT SIDEBAR ══════════════════════════════════════════════════ */}
    <div className="w-full lg:w-[360px] bg-white border-r border-gray-200 shadow-xl z-20 flex flex-col flex-shrink-0 no-print h-full transition-all duration-300">

      {/* Dark header */}
      <div className="no-print shrink-0 flex flex-col" style={{ background: '#20243B' }}>
        <div className="flex items-baseline gap-2 px-4 py-3">
          <span className="text-white font-bold text-sm tracking-wide">{title}</span>
        </div>
        <div className="h-0.5 w-full" style={{ background: '#C5A059' }} />
        {/* Tabs numeradas */}
        {tabs.length > 0 && (
          <div className="flex overflow-x-auto px-2 py-1.5 gap-0.5 border-b border-white/10">
            {tabs.map(({ id, label }, i) => (
              <button
                key={id}
                type="button"
                onClick={() => onTabChange?.(activeTab === id ? null : id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === id ? 'border-[#C5A059] text-[#C5A059] bg-white/10' : 'border-transparent text-white/50 hover:text-white/80'}`}
              >
                <span
                  className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-bold flex-shrink-0"
                  style={{ background: activeTab === id ? '#C5A059' : 'rgba(255,255,255,0.2)', color: 'white' }}
                >{i + 1}</span>
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* DOCUMENTO / ESTRUTURA toggle */}
      {onModeChange && (
        <div className="p-3 bg-white border-b border-gray-200 shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1">
              <button
                onClick={() => onModeChange('producao')}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === 'producao' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                type="button"
              >DOCUMENTO</button>
              <button
                onClick={() => onModeChange('auditoria')}
                className={`px-3 py-1.5 text-[11px] font-bold rounded-lg transition-colors ${mode === 'auditoria' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                type="button"
              >ESTRUTURA</button>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable config — children = seções accordion */}
      <div className="flex-1 overflow-y-auto custom-scrollbar px-2.5 py-2.5 bg-gray-50/80">
        {children}
      </div>

      {/* Bottom action bar */}
      {actionBar && (
        <div className="relative z-[260] p-3 border-t border-gray-200 bg-white shrink-0 space-y-2">
          <div className="grid grid-cols-1 gap-2 no-print">
            {actionBar}
          </div>
          {/* Sync status */}
          <div className="flex items-center justify-between gap-2 text-[9px] font-semibold uppercase tracking-[0.04em] text-gray-500 border-t border-gray-100 pt-1.5">
            <span>Sincronização automática</span>
            <SyncBadge state={syncState} meta={syncMeta} />
          </div>
          {error && (
            <div className="flex items-start gap-1.5 bg-red-50 border border-red-200 rounded-lg p-2">
              <AlertCircle className="w-3 h-3 text-red-500 shrink-0 mt-px" />
              <span className="text-[10px] text-red-700 leading-snug">{error}</span>
            </div>
          )}
        </div>
      )}
    </div>

    {/* ══ CENTER PANEL ══════════════════════════════════════════════════ */}
    {centerPanel}

    {/* ══ RIGHT PANEL (DOCUMENT MAP) ════════════════════════════════════ */}
    {rightPanel ? rightPanel : (rightPanelTitle && (
      <DocumentMapPanel
        title={rightPanelTitle}
        items={rightPanelItems}
        onItemClick={onRightPanelItemClick}
        onItemDelete={onRightPanelItemDelete}
      />
    ))}
  </div>
);

export default DocumentShell;
